import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Sentry } from '../../lib/sentry';
import { LanguageContext, type LanguageContextValue } from '../../features/language/LanguageContext';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  static contextType = LanguageContext;
  declare context: LanguageContextValue | null;
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  public render() {
    const t = this.context?.t;
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[400px] flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 rounded-lg border border-gray-200 m-4">
          <div className="max-w-md w-full space-y-8 text-center">
            <div>
              <h2 className="mt-6 text-center text-xl font-bold text-gray-900">
                {t ? t('common.errorBoundary.title') : 'common.errorBoundary.title'}
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                {this.state.error?.message || (t ? t('common.errorBoundary.unexpected') : 'common.errorBoundary.unexpected')}
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              {t ? t('common.errorBoundary.reload') : 'common.errorBoundary.reload'}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
